import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import api from '../../utils/api';

const defaultPlan = {
  name: '',
  price: '',
  duration: '',
  is_active: true,
  free_trial: false,
  free_trial_days: '',
  frameworks: [],
  channels: []
};

export default function PlanModal({ show, onHide, onSave, plan }) {
  const [form, setForm] = useState(defaultPlan);
  const [error, setError] = useState('');
  const [frameworkOptions, setFrameworkOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    api.get('/frameworks').then(res => setFrameworkOptions(res.data.frameworks || []));
    api.get('/channels').then(res => setChannelOptions(res.data.channels || []));
  }, []);

  useEffect(() => {
    async function fetchAndSetPlanMeta() {
      if (plan && plan.id) {
        setLoadingMeta(true);
        try {
          const res = await api.get(`/plans/${plan.id}/meta`);
          const plan_meta = res.data.meta || [];
          // Extract meta values from plan_meta
          let free_trial = false;
          let free_trial_days = '';
          let frameworks = [];
          let channels = [];
          for (const meta of plan_meta) {
            if (meta.meta_key === 'free_trial') free_trial = meta.meta_value === 'true' || meta.meta_value === true;
            if (meta.meta_key === 'free_trial_days') free_trial_days = meta.meta_value ? String(meta.meta_value) : '';
            if (meta.meta_key === 'frameworks') {
              try {
                const arr = typeof meta.meta_value === 'string' ? JSON.parse(meta.meta_value) : meta.meta_value;
                frameworks = Array.isArray(arr) ? arr.map(String) : [];
              } catch { frameworks = []; }
            }
            if (meta.meta_key === 'channels') {
              try {
                const arr = typeof meta.meta_value === 'string' ? JSON.parse(meta.meta_value) : meta.meta_value;
                channels = Array.isArray(arr) ? arr.map(String) : [];
              } catch { channels = []; }
            }
          }
          setForm({
            ...defaultPlan,
            ...plan,
            free_trial,
            free_trial_days,
            frameworks,
            channels
          });
        } catch {
          setForm({ ...defaultPlan, ...plan });
        } finally {
          setLoadingMeta(false);
        }
      } else if (plan) {
        setForm({ ...defaultPlan, ...plan });
      } else {
        setForm(defaultPlan);
      }
      setError('');
    }
    if (show) fetchAndSetPlanMeta();
  }, [plan, show]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && (name === 'frameworks' || name === 'channels')) {
      setForm(f => {
        const arr = new Set(f[name]);
        if (checked) arr.add(value);
        else arr.delete(value);
        return { ...f, [name]: Array.from(arr) };
      });
    } else if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name || !form.price || !form.duration) {
      setError('Name, price, and duration are required.');
      return;
    }
    // Always serialize frameworks/channels as JSON arrays for plan_meta
    const features = {
      free_trial: !!form.free_trial,
      free_trial_days: form.free_trial ? Number(form.free_trial_days) : 0,
      frameworks: form.frameworks.map(String),
      channels: form.channels.map(String)
    };
    onSave({
      ...form,
      features: JSON.stringify(features),
      free_trial: features.free_trial,
      free_trial_days: features.free_trial_days,
      frameworks: JSON.stringify(features.frameworks), // always JSON string
      channels: JSON.stringify(features.channels) // always JSON string
    });
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{plan ? 'Edit Plan' : 'Create Plan'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}
          <Form.Group className="mb-2">
            <Form.Label>Name</Form.Label>
            <Form.Control name="name" value={form.name} onChange={handleChange} required />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Price (USD)</Form.Label>
            <Form.Control name="price" type="number" value={form.price} onChange={handleChange} required />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Duration (days)</Form.Label>
            <Form.Control name="duration" type="number" value={form.duration} onChange={handleChange} required />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Check name="is_active" label="Active" checked={form.is_active} onChange={handleChange} />
          </Form.Group>
          <hr />
          <Form.Group className="mb-2">
            <Form.Check name="free_trial" label="Free Trial?" checked={form.free_trial} onChange={handleChange} />
          </Form.Group>
          {form.free_trial && (
            <Form.Group className="mb-2">
              <Form.Label>Free Trial Days</Form.Label>
              <Form.Control name="free_trial_days" type="number" value={form.free_trial_days} onChange={handleChange} min={1} />
            </Form.Group>
          )}
          <Form.Group className="mb-2">
            <Form.Label>Choose Framework(s)</Form.Label>
            <div>
              {frameworkOptions.map(fw => (
                <Form.Check
                  key={fw.id}
                  type="checkbox"
                  name="frameworks"
                  value={fw.id}
                  label={fw.name}
                  checked={form.frameworks.includes(fw.id.toString()) || form.frameworks.includes(fw.id) }
                  onChange={handleChange}
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Choose Channels</Form.Label>
            <div>
              {channelOptions.map(ch => (
                <Form.Check
                  key={ch.id}
                  type="checkbox"
                  name="channels"
                  value={ch.id}
                  label={ch.name}
                  checked={form.channels.includes(ch.id.toString()) || form.channels.includes(ch.id) }
                  onChange={handleChange}
                />
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" variant="primary">{plan ? 'Update' : 'Create'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
